import {isEqual} from 'lodash-es';
import {defer, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {isExchangeAccountBaseEqual} from '../../models/exchange-account';
import {clearSecurityKey, isSecurityKeysEqual} from '../../models/security-key';
import {getModelWithUpdateField, hasModelUpdateField, isModelUpdateEmptyOrSame} from './correction-common';
import {CCtxDeps, CModelDeps, CorrectionFnDeps} from './correction-dependencies';

export const isModelUpdatesSecurityKey = <T extends CModelDeps<'securityKey'>>(
    modelUpdate: Partial<T>,
    prevModel: Partial<T>
): boolean =>
    !!modelUpdate?.securityKey
    && !isSecurityKeysEqual(modelUpdate.securityKey, prevModel?.securityKey);

/**
 * Returns true if SecurityKey updated and SecurityExchange updated too
 * @returns {boolean}
 */
export const isModelUpdatesSecurityKeyIncludeExchange = <T extends CModelDeps<'securityKey'>>(
    modelUpdate: Partial<T>,
    prevModel: Partial<T>
): boolean => {
    const currentSecurityKey = prevModel?.securityKey;

    return isModelUpdatesSecurityKey(modelUpdate, prevModel)
        && (
            !currentSecurityKey
            || currentSecurityKey.securityExchange !== modelUpdate.securityKey.securityExchange
        );
};

export const getModelSecurityKeyFix: CorrectionFnDeps<'securityKey', 'securityService'> = (
    modelUpdate: Partial<CModelDeps<'securityKey'>>,
    prevModel: CModelDeps<'securityKey'>,
    ctx: CCtxDeps<'securityService'>
): Observable<Partial<CModelDeps<'securityKey'>>> =>
    defer(() =>
        isModelUpdatesSecurityKey(modelUpdate, prevModel)
            ? ctx.securityService.getSecurityBySecurityKey(modelUpdate.securityKey).pipe(
                switchMap(security => security
                    ? of(null)
                    : ctx.securityService.getDefaultSecurity().pipe(
                        map(defaultSecurity => ({
                                securityKey: clearSecurityKey(defaultSecurity)
                            })
                        )
                    )
                )
            )
            : of(null)
    );

export const getModelAccountFixBySecurity:
                 CorrectionFnDeps<'securityKey' | 'account', 'securityService' | 'accountService'> =(
    modelUpdate,
    prevModel,
    ctx
) => {
    let correction: Partial<CModelDeps<'account'>> | undefined;
    const isAccountChanged = hasModelUpdateField(modelUpdate, 'account')
        && !isExchangeAccountBaseEqual(prevModel?.account, modelUpdate.account);
    const isAccountOrExchangeChanged = isAccountChanged
        || isModelUpdatesSecurityKeyIncludeExchange(modelUpdate, prevModel);

    if (isAccountOrExchangeChanged) {
        const settingsAccountBase = getModelWithUpdateField(prevModel, modelUpdate, 'account');
        const settingsSecurityKey = getModelWithUpdateField(prevModel, modelUpdate, 'securityKey');

        if (settingsSecurityKey) {
            const correctAccountBase = ctx.accountService.validateAccountBaseOrDefaultFromCache(
                settingsAccountBase,
                settingsSecurityKey.securityExchange
            );

            if (!isExchangeAccountBaseEqual(correctAccountBase, settingsAccountBase)) {
                correction = {
                    account: correctAccountBase
                };
            }
        }
    }

    return of(correction);
};

export const getLeverageFix: CorrectionFnDeps<'securityKey' | 'account' | 'leverageBySide',
    'securityService' | 'accountService' | 'leverageService'> = (
    modelUpdate,
    prevModel,
    ctx
) => {
    const isCorrectionNeeded = !isModelUpdateEmptyOrSame(prevModel, modelUpdate,
        ['securityKey', isSecurityKeysEqual as any],
        ['account', isExchangeAccountBaseEqual as any],
        ['leverageBySide', isEqual]
    );

    let result$: Observable<Partial<CModelDeps<'leverageBySide'>>>;

    if (isCorrectionNeeded) {
        const securityKey = getModelWithUpdateField(prevModel, modelUpdate, 'securityKey');

        result$ = ctx.securityService.getSecurityBySecurityKey(securityKey).pipe(
            switchMap(security =>
                ctx.leverageService.correctLeverageBySideMap(
                    security,
                    getModelWithUpdateField(prevModel, modelUpdate, 'account'),
                    getModelWithUpdateField(prevModel, modelUpdate, 'leverageBySide')
                ).pipe(
                    map(leverageBySide => ({
                        leverageBySide
                    }))
                )
            )
        );
    } else {
        result$ = of({});
    }

    return result$;
};

export const getModelInvPortfolioFix: CorrectionFnDeps<'investmentEntityId' | 'portfolioId', 'invPortfolioService'> = (
    modelUpdate,
    prevModel,
    ctx
) => {
    const isCorrectionNeeded = !isModelUpdateEmptyOrSame(prevModel, modelUpdate, 'investmentEntityId', 'portfolioId');

    let result: Partial<CModelDeps<'investmentEntityId' | 'portfolioId'>>;

    if (isCorrectionNeeded) {
        const invEntityId = getModelWithUpdateField(prevModel, modelUpdate, 'investmentEntityId');
        const portfolioId = getModelWithUpdateField(prevModel, modelUpdate, 'portfolioId');

        const correctInvEntity = ctx.invPortfolioService.getInvEntity(invEntityId);
        const correctPortfolio = correctInvEntity
            && ctx.invPortfolioService.getPortfolio(correctInvEntity.id, portfolioId);

        result = {
            investmentEntityId: correctInvEntity ? correctInvEntity.id : invEntityId,
            portfolioId:        correctPortfolio ? correctPortfolio.id : portfolioId
        };
    } else {
        result = {};
    }

    return of(result);
};

